import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { RefreshCw } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { Skeleton } from '../../../components/ui/Skeleton';
import { api, ApiError } from '../../../lib/api';

interface RefStatus {
  configured: boolean;
  kinds: string[];
  reference_fetched_at: Record<string, string>;
}

interface RefreshResult {
  results: { kind: string; count?: number; error?: string }[];
}

const KIND_LABELS: Record<string, string> = {
  roster: 'People roster',
  picklist: 'Picklists',
  cost_centre: 'Cost centres',
  country: 'Countries',
  department: 'Departments',
  employment_type: 'Employment types',
  trade: 'Trades',
  tariff_group: 'Tariff groups',
  position: 'Positions',
  property: 'Properties',
};

/**
 * Refreshes the cached Planon reference data that feeds the create-person
 * dropdowns. Lives on the Planon admin page beside the asset-sync status.
 */
export function PlanonReferencePanel() {
  const qc = useQueryClient();
  const status = useQuery({
    queryKey: ['planon-ref-status'],
    queryFn: () => api<RefStatus>('/api/admin/planon/reference/status'),
  });

  const refresh = useMutation({
    mutationFn: () =>
      api<RefreshResult>('/api/admin/planon/reference/refresh', {
        method: 'POST',
        body: JSON.stringify({ kind: 'all' }),
      }),
    onSuccess: (r) => {
      const failed = r.results.filter((x) => x.error);
      if (failed.length > 0) toast.error(`${failed.length} of ${r.results.length} kinds failed to refresh`);
      else toast.success('Planon reference data refreshed');
      void qc.invalidateQueries({ queryKey: ['planon-ref-status'] });
      void qc.invalidateQueries({ queryKey: ['planon-reference'] });
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'refresh failed'),
  });

  const fmt = (iso: string | undefined) =>
    iso ? new Date(iso).toLocaleString() : 'never';

  return (
    <section className="mt-4 overflow-hidden rounded-lg border border-line/10 bg-surface shadow-sm">
      <div className="flex items-center justify-between border-b border-line/10 px-5 py-3">
        <h2 className="text-sm font-semibold">Reference data</h2>
        <Button
          variant="secondary"
          size="sm"
          iconLeft={<RefreshCw className="h-4 w-4" />}
          loading={refresh.isPending}
          disabled={!status.data?.configured}
          onClick={() => refresh.mutate()}
        >
          Refresh all
        </Button>
      </div>
      <div className="px-5 py-4">
        <p className="mb-3 text-sm text-fg-muted">
          The roster (copy-from) and dropdown options for creating a person. Refresh after any change
          in Planon.
        </p>
        {status.isLoading ? (
          <Skeleton className="h-24 w-full" />
        ) : (
          <div className="grid grid-cols-1 gap-x-6 gap-y-1.5 sm:grid-cols-2">
            {(status.data?.kinds ?? []).map((kind) => (
              <div key={kind} className="flex items-center justify-between text-sm">
                <span className="text-fg">{KIND_LABELS[kind] ?? kind}</span>
                <span className="text-xs text-fg-dim">{fmt(status.data?.reference_fetched_at[kind])}</span>
              </div>
            ))}
          </div>
        )}
        {status.data && !status.data.configured ? (
          <p className="mt-3 text-xs text-warning-soft-fg">
            Planon is not configured, so refresh is disabled.
          </p>
        ) : null}
      </div>
    </section>
  );
}
